import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { resolveAnyStaticUrl } from '../../urlConfig';
import {
    getCatalogItem,
    updateCatalogItem,
} from './catalogReviewApi';
import AdminProduct3DPreview from '../ComponentContainer/AdminProduct3DPreview/AdminProduct3DPreview';

const CatalogPricingReviewContext = createContext(null);

const EMPTY_FORM = {
    height: '',
    width: '',
    thickness: '',
    price: '',
    comment: '',
};

const REVIEWED_STATUSES = new Set([
    'approved',
    'edited',
    'manual',
    'source-confirmed',
]);

const LOCAL_DRAFT_FIELDS = new Set([
    'height',
    'width',
    'thickness',
    'price',
    'comment',
    'proportional',
    'dimensionSource',
]);

const uniqueNumbers = (values = []) => Array.from(new Set(
    (Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
));

const formatMoney = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
        ? `${number.toLocaleString('ru-RU')} ₽`
        : '—';
};

const formatSize = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    return height > 0 && width > 0 && thickness > 0
        ? `${height}×${width}×${thickness} мм`
        : '—';
};

const getErrorMessage = (error) => (
    error?.response?.data?.message
    || error?.message
    || 'Произошла ошибка'
);

const reviewStatusLabel = (status) => {
    if (status === 'approved') return 'Подтверждено';
    if (status === 'edited') return 'Исправлено';
    if (status === 'manual') return 'Ручная проверка';
    if (status === 'source-confirmed') return 'Подтверждено ведомостью';
    return 'Не проверено';
};

const getLastPricingAudit = (item = {}) => {
    const logs = Array.isArray(item?.auditLog) ? item.auditLog : [];
    const entry = [...logs].reverse().find((row) => row?.action === 'pricing-update');

    if (entry?.userName) return entry;
    if (item?.updatedByName && item?.reviewedAt) {
        return {
            userId: item.updatedBy || null,
            userName: item.updatedByName,
            createdAt: item.reviewedAt,
        };
    }
    return null;
};

const formatAuditTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const sourceLabel = (item = {}) => {
    if (item.catalogSource?.type === 'manual-product') return 'Добавлен вручную';
    if (item.catalogSource?.type === 'new-catalog-product') {
        return 'Новый каталог';
    }

    if (item.sourcePricingStatus === 'Подтверждено') {
        return 'Подтверждено ведомостью';
    }

    return item.sourcePricingStatus || 'Оценка';
};


const positive = (value) => Number(value) > 0;

const hasDraftValue = (value) => (
    value !== ''
    && value !== null
    && value !== undefined
);

const normalizeDimensions = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    if (!(height > 0) || !(width > 0) || !(thickness > 0)) {
        return null;
    }

    return {
        height: Math.round(height),
        width: Math.round(width),
        thickness: Math.round(thickness),
        unit: 'mm',
    };
};

const sameDimensions = (left, right) => {
    const a = normalizeDimensions(left);
    const b = normalizeDimensions(right);

    if (!a || !b) return false;

    return (
        a.height === b.height
        && a.width === b.width
        && a.thickness === b.thickness
    );
};

const getMeasuredModelDimensions = (item = {}) => {
    if (item?.geometryAnalysis?.status !== 'measured') {
        return null;
    }

    return normalizeDimensions(item?.geometryAnalysis?.dimensions);
};

const CatalogPricingReviewModal = ({
    request,
    currentNumber,
    onCurrentNumberChange,
    onClose,
    remotePatch,
    realtimeState,
    itemUpdate,
}) => {
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [proportional, setProportional] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [dimensionSource, setDimensionSource] = useState('model');
    const [activeView, setActiveView] = useState(
        request?.initialView === '3d' ? '3d' : 'pricing'
    );
    const [viewerDimensions, setViewerDimensions] = useState(null);

    const ratioRef = useRef(null);
    const mountedRef = useRef(true);
    const previousViewRef = useRef(activeView);
    const loadRequestIdRef = useRef(0);
    const pendingRemoteDraftRef = useRef({
        entityNumber: null,
        revision: -1,
        fields: {},
    });
    const lastAppliedWorkspaceRevisionRef = useRef(-1);
    const localEditingFieldsRef = useRef(new Set());
    const recentLocalDraftsRef = useRef(new Map());

    if (
        Number(pendingRemoteDraftRef.current.entityNumber || 0)
        !== Number(currentNumber || 0)
    ) {
        pendingRemoteDraftRef.current = {
            entityNumber: Number(currentNumber || 0),
            revision: -1,
            fields: {},
        };
        lastAppliedWorkspaceRevisionRef.current = -1;
    }

    const queueNumbers = request?.queueNumbers || [];
    const realtimeOperations = realtimeState?.operations || {};
    const saveOperationKey = `pricing:save:${Number(currentNumber || 0)}`;
    const remoteSaving = Boolean(realtimeOperations[saveOperationKey]);
    const effectiveSaving = saving || remoteSaving;
    const authoritativeWorkspace = realtimeState?.workspace || null;
    const authoritativePricingDraft = authoritativeWorkspace?.pricingDraft || null;

    const currentIndex = Math.max(
        0,
        queueNumbers.findIndex((number) => Number(number) === Number(currentNumber))
    );
    const hasPrevious = queueNumbers.length > 0 && currentIndex > 0;
    const hasNext = queueNumbers.length > 0 && currentIndex < queueNumbers.length - 1;

    const item = detail?.item || null;
    const lastPriceEdit = useMemo(() => getLastPricingAudit(item), [item]);
    const linkedProduct = detail?.linkedProduct || item?.productLink?.product || null;
    const manualReview = item?.catalogSource?.type === 'manual-product';
    const linkedProductName = (
        linkedProduct?.fullName
        || linkedProduct?.name
        || ''
    );
    const linkedProductCategory = (
        linkedProduct?.categoryName
        || linkedProduct?.categorySlug
        || ''
    );
    const initial = item?.pricingDraft || {};
    const initialDimensions = useMemo(
        () => normalizeDimensions(initial.baseDimensions),
        [
            initial.baseDimensions?.height,
            initial.baseDimensions?.width,
            initial.baseDimensions?.thickness,
        ]
    );

    const modelDimensions = useMemo(
        () => getMeasuredModelDimensions(item),
        [
            item?.geometryAnalysis?.status,
            item?.geometryAnalysis?.dimensions?.height,
            item?.geometryAnalysis?.dimensions?.width,
            item?.geometryAnalysis?.dimensions?.thickness,
        ]
    );

    const baselineDimensions = useMemo(() => (
        REVIEWED_STATUSES.has(String(item?.reviewStatus || ''))
            ? (initialDimensions || modelDimensions)
            : modelDimensions
    ), [
        initialDimensions,
        item?.reviewStatus,
        modelDimensions,
    ]);

    const currentDimensions = useMemo(() => ({
        height: Number(form.height || 0),
        width: Number(form.width || 0),
        thickness: Number(form.thickness || 0),
        unit: 'mm',
    }), [form.height, form.width, form.thickness]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setViewerDimensions(currentDimensions);
        }, activeView === '3d' ? 120 : 0);

        return () => window.clearTimeout(timer);
    }, [
        activeView,
        currentDimensions.height,
        currentDimensions.width,
        currentDimensions.thickness,
    ]);

    const keepLocalDraftValue = useCallback((field, incomingValue) => {
        if (!LOCAL_DRAFT_FIELDS.has(field)) return false;

        if (localEditingFieldsRef.current.has(field)) {
            return true;
        }

        const recent = recentLocalDraftsRef.current.get(field);
        if (!recent) return false;

        const sameValue = String(recent.value ?? '')
            === String(incomingValue ?? '');

        if (sameValue) {
            recentLocalDraftsRef.current.delete(field);
            return false;
        }

        return Date.now() - Number(recent.changedAt || 0) < 1000;
    }, []);

    const publishDraft = useCallback((field, value) => {
        if (LOCAL_DRAFT_FIELDS.has(field)) {
            recentLocalDraftsRef.current.set(field, {
                value,
                changedAt: Date.now(),
            });
        }

        request?.onDraftChange?.({
            purchasedNumber: Number(currentNumber || 0),
            field,
            value,
        });
    }, [currentNumber, request]);

    const focusDraftField = useCallback((_event, field) => {
        localEditingFieldsRef.current.add(field);
    }, []);

    const blurDraftField = useCallback((field) => {
        localEditingFieldsRef.current.delete(field);
    }, []);

    const clearLocalEditingFields = useCallback(() => {
        localEditingFieldsRef.current.clear();
    }, []);

    const closeReview = useCallback(() => {
        clearLocalEditingFields();
        onClose();
    }, [
        clearLocalEditingFields,
        onClose,
    ]);

    /*
     * Базой служит измеренный размер 3D-модели. Ранее сохранённый размер
     * используем только у уже проверенных форм, чтобы ручная корректировка
     * не пропадала при повторном открытии карточки.
     */
    const initialHeight = Number(baselineDimensions?.height || 0);
    const initialWidth = Number(baselineDimensions?.width || 0);
    const initialThickness = Number(baselineDimensions?.thickness || 0);

    const changed = Boolean(item) && (
        Number(form.height) !== initialHeight
        || Number(form.width) !== initialWidth
        || Number(form.thickness) !== initialThickness
        || Number(form.price) !== Number(initial.basePrice)
        || String(form.comment || '') !== String(item.reviewComment || '')
    );

    const valid = (
        positive(form.height)
        && positive(form.width)
        && positive(form.thickness)
        && Number.isFinite(Number(form.price))
        && Number(form.price) >= 0
    );

    const currentVolume = valid
        ? (
            Number(form.height)
            * Number(form.width)
            * Number(form.thickness)
            / 1000000000
        )
        : null;

    const estimateRange = item?.estimate?.priceRange || {};

    const loadCurrent = useCallback(async () => {
        if (!currentNumber) return;

        const requestId = loadRequestIdRef.current + 1;
        loadRequestIdRef.current = requestId;
        setLoading(true);
        setMessage(null);

        try {
            const result = await getCatalogItem(currentNumber);

            if (
                !mountedRef.current
                || requestId !== loadRequestIdRef.current
            ) return;

            const nextItem = result?.item || null;
            const savedDimensions = normalizeDimensions(
                nextItem?.pricingDraft?.baseDimensions
            );
            const measuredDimensions = getMeasuredModelDimensions(nextItem);
            const reviewedDimensions = REVIEWED_STATUSES.has(
                String(nextItem?.reviewStatus || '')
            )
                ? savedDimensions
                : null;

            /*
             * Непроверенные формы всегда начинают с реального размера 3D.
             * Предполагаемый размер каталога не показываем и не подставляем.
             * У уже проверенной формы сохраняем сделанную вручную правку.
             */
            const preferredDimensions = reviewedDimensions
                || measuredDimensions
                || {
                    height: '',
                    width: '',
                    thickness: '',
                };

            const sharedDraft = (
                Number(request?.initialDraft?.purchasedNumber || 0)
                === Number(currentNumber)
            )
                ? request.initialDraft
                : null;
            const nextForm = {
                height: hasDraftValue(sharedDraft?.height)
                    ? sharedDraft.height
                    : (preferredDimensions.height ?? ''),
                width: hasDraftValue(sharedDraft?.width)
                    ? sharedDraft.width
                    : (preferredDimensions.width ?? ''),
                thickness: hasDraftValue(sharedDraft?.thickness)
                    ? sharedDraft.thickness
                    : (preferredDimensions.thickness ?? ''),
                price: hasDraftValue(sharedDraft?.price)
                    ? sharedDraft.price
                    : (nextItem?.pricingDraft?.basePrice ?? ''),
                comment: hasDraftValue(sharedDraft?.comment)
                    ? sharedDraft.comment
                    : (nextItem?.reviewComment || ''),
            };
            const pendingRemote = pendingRemoteDraftRef.current;
            if (Number(pendingRemote.entityNumber) === Number(currentNumber)) {
                Object.assign(nextForm, pendingRemote.fields || {});
            }
            const pendingFields = (
                Number(pendingRemote.entityNumber) === Number(currentNumber)
                    ? pendingRemote.fields || {}
                    : {}
            );
            const sharedDimensionSource = pendingFields.dimensionSource
                ?? sharedDraft?.dimensionSource;
            const defaultDimensionSource = (
                reviewedDimensions
                && !sameDimensions(reviewedDimensions, measuredDimensions)
            )
                ? 'custom'
                : 'model';
            const nextDimensionSource = String(sharedDimensionSource || '') === 'custom'
                ? 'custom'
                : defaultDimensionSource;
            const nextProportional = Object.prototype.hasOwnProperty.call(
                pendingFields,
                'proportional'
            )
                ? pendingFields.proportional === true
                : sharedDraft?.proportional === true;

            setDetail(result);
            setDimensionSource(nextDimensionSource);
            setProportional(nextProportional);
            setForm(nextForm);

            const sharedDraftReady = (
                Number(sharedDraft?.purchasedNumber || 0) === Number(currentNumber)
                && hasDraftValue(sharedDraft?.height)
                && hasDraftValue(sharedDraft?.width)
                && hasDraftValue(sharedDraft?.thickness)
                && hasDraftValue(sharedDraft?.price)
            );

            if (!sharedDraftReady) {
                await request?.onDraftInitialize?.({
                    purchasedNumber: Number(currentNumber),
                    draft: {
                        ...nextForm,
                        proportional: nextProportional,
                        dimensionSource: nextDimensionSource,
                        viewerMode: sharedDraft?.viewerMode || 'light',
                        showDimensions: sharedDraft?.showDimensions !== false,
                        showBlankDimensions: sharedDraft?.showBlankDimensions === true,
                        showBlankFrame: sharedDraft?.showBlankFrame !== false,
                        stageFullscreen: sharedDraft?.stageFullscreen === true,
                        displayControlsOpen: sharedDraft?.displayControlsOpen === true,
                        sunAngle: Number(sharedDraft?.sunAngle ?? 135),
                        sunVertical: Number(sharedDraft?.sunVertical ?? 38),
                        sunIntensity: Number(sharedDraft?.sunIntensity ?? 2.8),
                        fillIntensity: Number(sharedDraft?.fillIntensity ?? 0.7),
                        hemisphereIntensity: Number(sharedDraft?.hemisphereIntensity ?? 0.7),
                        ambientIntensity: Number(sharedDraft?.ambientIntensity ?? 0.35),
                        rimIntensity: Number(sharedDraft?.rimIntensity ?? 0.6),
                        shadowsEnabled: sharedDraft?.shadowsEnabled !== false,
                        graphicsQuality: sharedDraft?.graphicsQuality || 'medium',
                        backgroundMode: sharedDraft?.backgroundMode || 'theme',
                        backgroundUrl: sharedDraft?.backgroundUrl || '',
                        backgroundAsEnvironment: sharedDraft?.backgroundAsEnvironment === true,
                        showGround: sharedDraft?.showGround !== false,
                        backgroundTheme: sharedDraft?.backgroundTheme || sharedDraft?.viewerMode || 'light',
                        dimensionLabelScale: Number(
                            sharedDraft?.dimensionLabelScale ?? 1.35
                        ),
                    },
                });
            }

            const height = Number(nextForm.height || 0);
            const width = Number(nextForm.width || 0);
            ratioRef.current = height > 0 && width > 0
                ? width / height
                : null;
        } catch (error) {
            if (!mountedRef.current) return;
            setDetail(null);
            setMessage({
                type: 'error',
                text: getErrorMessage(error),
            });
        } finally {
            if (
                mountedRef.current
                && requestId === loadRequestIdRef.current
            ) setLoading(false);
        }
    }, [currentNumber, request]);

    useEffect(() => {
        mountedRef.current = true;
        loadCurrent();

        return () => {
            mountedRef.current = false;
        };
    }, [loadCurrent]);

    useEffect(() => {
        localEditingFieldsRef.current.clear();
        recentLocalDraftsRef.current.clear();
    }, [currentNumber]);

    useEffect(() => () => {
        clearLocalEditingFields();
    }, [clearLocalEditingFields]);

    useEffect(() => {
        if (
            !remotePatch
            || remotePatch.scope !== 'pricing'
            || Number(remotePatch.entityNumber) !== Number(currentNumber)
        ) {
            return;
        }

        const { field, value } = remotePatch;
        const workspaceRevision = Number(remotePatch.workspaceRevision || 0);

        if (keepLocalDraftValue(field, value)) {
            return;
        }

        if (
            workspaceRevision
            && workspaceRevision < lastAppliedWorkspaceRevisionRef.current
        ) {
            return;
        }

        if (workspaceRevision) {
            lastAppliedWorkspaceRevisionRef.current = workspaceRevision;
        }

        const pending = pendingRemoteDraftRef.current;
        pendingRemoteDraftRef.current = {
            entityNumber: Number(currentNumber),
            revision: Math.max(Number(pending.revision || -1), workspaceRevision),
            fields: {
                ...(Number(pending.entityNumber) === Number(currentNumber)
                    ? pending.fields
                    : {}),
                [field]: value,
            },
        };

        if (['height', 'width', 'thickness', 'price', 'comment'].includes(field)) {
            setForm((previous) => {
                const next = {
                    ...previous,
                    [field]: value,
                };

                if (field === 'height' || field === 'width') {
                    const nextHeight = Number(next.height || 0);
                    const nextWidth = Number(next.width || 0);

                    if (nextHeight > 0 && nextWidth > 0) {
                        ratioRef.current = nextWidth / nextHeight;
                    }
                }

                return next;
            });
        } else if (field === 'proportional') {
            setProportional(value === true);
        } else if (field === 'dimensionSource') {
            setDimensionSource(String(value) === 'model' ? 'model' : 'custom');
        }
    }, [
        currentNumber,
        keepLocalDraftValue,
        remotePatch,
    ]);

    useEffect(() => {
        const revision = Number(authoritativeWorkspace?.revision || 0);
        const draft = authoritativePricingDraft;

        if (
            !draft
            || Number(draft.purchasedNumber || 0) !== Number(currentNumber)
            || revision < lastAppliedWorkspaceRevisionRef.current
        ) {
            return;
        }

        const hasAuthoritativeDimensions = [
            draft.height,
            draft.width,
            draft.thickness,
        ].some(hasDraftValue);

        const applyHeight = hasAuthoritativeDimensions
            && !keepLocalDraftValue('height', draft.height ?? '');
        const applyWidth = hasAuthoritativeDimensions
            && !keepLocalDraftValue('width', draft.width ?? '');
        const applyThickness = hasAuthoritativeDimensions
            && !keepLocalDraftValue('thickness', draft.thickness ?? '');
        const applyPrice = hasDraftValue(draft.price)
            && !keepLocalDraftValue('price', draft.price);
        const applyComment = !keepLocalDraftValue(
            'comment',
            draft.comment ?? ''
        );
        const applyProportional = !keepLocalDraftValue(
            'proportional',
            draft.proportional === true
        );
        const normalizedDimensionSource = String(draft.dimensionSource) === 'custom'
            ? 'custom'
            : 'model';
        const applyDimensionSource = !keepLocalDraftValue(
            'dimensionSource',
            normalizedDimensionSource
        );

        const pendingFields = {};

        if (applyHeight) pendingFields.height = draft.height ?? '';
        if (applyWidth) pendingFields.width = draft.width ?? '';
        if (applyThickness) pendingFields.thickness = draft.thickness ?? '';
        if (applyPrice) pendingFields.price = draft.price;
        if (applyComment) pendingFields.comment = draft.comment ?? '';
        if (applyProportional) {
            pendingFields.proportional = draft.proportional === true;
        }
        if (applyDimensionSource) {
            pendingFields.dimensionSource = normalizedDimensionSource;
        }

        lastAppliedWorkspaceRevisionRef.current = revision;
        pendingRemoteDraftRef.current = {
            entityNumber: Number(currentNumber),
            revision,
            fields: pendingFields,
        };

        setForm((previous) => {
            const next = {
                ...previous,
                height: applyHeight ? (draft.height ?? '') : previous.height,
                width: applyWidth ? (draft.width ?? '') : previous.width,
                thickness: applyThickness
                    ? (draft.thickness ?? '')
                    : previous.thickness,
                price: applyPrice ? draft.price : previous.price,
                comment: applyComment
                    ? (draft.comment ?? '')
                    : previous.comment,
            };

            return (
                next.height === previous.height
                && next.width === previous.width
                && next.thickness === previous.thickness
                && next.price === previous.price
                && next.comment === previous.comment
            ) ? previous : next;
        });

        if (applyProportional) {
            setProportional(draft.proportional === true);
        }

        if (applyDimensionSource) {
            setDimensionSource(normalizedDimensionSource);
        }
    }, [
        authoritativePricingDraft,
        authoritativeWorkspace?.revision,
        currentNumber,
        keepLocalDraftValue,
    ]);


    useEffect(() => {
        const updatedItem = itemUpdate?.item;
        if (
            !updatedItem
            || Number(updatedItem.purchasedNumber || 0) !== Number(currentNumber)
        ) return;

        setDetail((previous) => (
            previous
                ? {
                    ...previous,
                    item: {
                        ...(previous.item || {}),
                        ...updatedItem,
                    },
                }
                : previous
        ));
    }, [currentNumber, itemUpdate]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape' || effectiveSaving) return;

            if (activeView === '3d') {
                setActiveView('pricing');
                return;
            }

            closeReview();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('catalog-review-modal-open');
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.documentElement.classList.remove('catalog-review-modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        activeView,
        closeReview,
        effectiveSaving,
    ]);

    const changeDimension = useCallback((field, rawValue) => {
        const next = {
            ...form,
            [field]: rawValue,
        };

        if (proportional && ['height', 'width'].includes(field)) {
            const ratio = Number(ratioRef.current || 0);
            const numeric = Number(rawValue);

            if (ratio > 0 && numeric > 0) {
                if (field === 'height') {
                    next.width = String(Math.max(1, Math.round(numeric * ratio)));
                } else {
                    next.height = String(Math.max(1, Math.round(numeric / ratio)));
                }
            }
        }

        setDimensionSource('custom');
        setForm(next);

        publishDraft('dimensionSource', 'custom');
        publishDraft(field, rawValue);

        if (next.height !== form.height && field !== 'height') {
            publishDraft('height', next.height);
        }
        if (next.width !== form.width && field !== 'width') {
            publishDraft('width', next.width);
        }
    }, [form, proportional, publishDraft]);

    const toggleProportional = useCallback((checked) => {
        setProportional(checked);
        publishDraft('proportional', checked);

        if (!checked) return;

        const height = Number(form.height || 0);
        const width = Number(form.width || 0);

        if (height > 0 && width > 0) {
            ratioRef.current = width / height;
        }
    }, [form.height, form.width, publishDraft]);

    const previousNumber = useMemo(() => {
        if (!hasPrevious) return null;
        return queueNumbers[currentIndex - 1] || null;
    }, [currentIndex, hasPrevious, queueNumbers]);

    const nextNumber = useMemo(() => {
        if (!hasNext) return null;
        return queueNumbers[currentIndex + 1] || null;
    }, [currentIndex, hasNext, queueNumbers]);

    const movePrevious = useCallback(() => {
        if (!previousNumber) return false;

        clearLocalEditingFields();
        onCurrentNumberChange(previousNumber, { view: activeView });
        return true;
    }, [
        activeView,
        onCurrentNumberChange,
        previousNumber,
        clearLocalEditingFields,
    ]);

    const moveNext = useCallback(() => {
        if (nextNumber) {
            clearLocalEditingFields();
            onCurrentNumberChange(nextNumber, { view: activeView });
            return true;
        }

        setMessage({
            type: 'success',
            text: 'Подборка закончена. Все выбранные формы просмотрены.',
        });
        return false;
    }, [
        activeView,
        nextNumber,
        onCurrentNumberChange,
        clearLocalEditingFields,
    ]);

    const buildPatch = useCallback((reviewStatus) => ({
        reviewStatus,
        reviewComment: form.comment,
        changedFromEstimate: changed,
        pricingDraft: {
            baseDimensions: {
                height: Number(form.height),
                width: Number(form.width),
                thickness: Number(form.thickness),
                unit: 'mm',
            },
            basePrice: Number(form.price),
            sizeRules: [{
                dimensions: {
                    height: Number(form.height),
                    width: Number(form.width),
                    thickness: Number(form.thickness),
                    unit: 'mm',
                },
                price: Number(form.price),
                enabled: true,
                estimated: item?.sourcePricingStatus === 'Оценка',
                note: form.comment,
            }],
        },
    }), [
        changed,
        form.comment,
        form.height,
        form.price,
        form.thickness,
        form.width,
        item?.sourcePricingStatus,
    ]);

    const save = useCallback(async (requestedStatus) => {
        if (!item || !valid || effectiveSaving) return;

        setSaving(true);
        setMessage(null);
        realtimeState?.publishOperation?.({
            key: saveOperationKey,
            running: true,
        });

        const preservedStatus = (
            !changed
            && REVIEWED_STATUSES.has(String(item.reviewStatus || ''))
            && item.reviewStatus !== 'manual'
        )
            ? item.reviewStatus
            : requestedStatus;

        try {
            const saveItem = request?.saveItem || (async ({
                purchasedNumber,
                revision,
                patch,
            }) => updateCatalogItem(purchasedNumber, revision, patch));

            const result = await saveItem({
                purchasedNumber: item.purchasedNumber,
                revision: item.revision,
                patch: buildPatch(preservedStatus),
            });

            const savedItem = result?.item || result;

            if (savedItem?._id) {
                setDetail((previous) => ({
                    ...(previous || {}),
                    item: savedItem,
                }));
            }

            await request?.onSaved?.({
                item: savedItem,
                result,
                purchasedNumber: item.purchasedNumber,
            });

            if (!moveNext()) {
                setMessage({
                    type: 'success',
                    text: 'Размер и цена сохранены. Подборка закончена.',
                });
            }
        } catch (error) {
            const latest = error?.latest || error?.response?.data?.latest;

            if (latest?._id) {
                setDetail((previous) => ({
                    ...(previous || {}),
                    item: latest,
                }));
            }

            setMessage({
                type: 'error',
                text: getErrorMessage(error),
            });
        } finally {
            setSaving(false);
            realtimeState?.publishOperation?.({
                key: saveOperationKey,
                running: false,
            });
        }
    }, [
        buildPatch,
        changed,
        item,
        moveNext,
        request,
        effectiveSaving,
        realtimeState,
        saveOperationKey,
        valid,
    ]);

    const resetToUnchecked = useCallback(async () => {
        if (!item || effectiveSaving || item.reviewStatus === 'unchecked') return;

        setSaving(true);
        setMessage(null);
        realtimeState?.publishOperation?.({
            key: saveOperationKey,
            running: true,
        });

        try {
            const saveItem = request?.saveItem || (async ({
                purchasedNumber,
                revision,
                patch,
            }) => updateCatalogItem(purchasedNumber, revision, patch));

            const result = await saveItem({
                purchasedNumber: item.purchasedNumber,
                revision: item.revision,
                patch: {
                    reviewStatus: 'unchecked',
                },
            });

            const savedItem = result?.item || result;

            if (savedItem?._id) {
                setDetail((previous) => ({
                    ...(previous || {}),
                    item: savedItem,
                }));
            }

            await request?.onSaved?.({
                item: savedItem,
                result,
                purchasedNumber: item.purchasedNumber,
            });

            setMessage({
                type: 'success',
                text: 'Форма возвращена в непроверенные. Размер и цена сохранены без изменений.',
            });
        } catch (error) {
            const latest = error?.latest || error?.response?.data?.latest;

            if (latest?._id) {
                setDetail((previous) => ({
                    ...(previous || {}),
                    item: latest,
                }));
            }

            setMessage({
                type: 'error',
                text: getErrorMessage(error),
            });
        } finally {
            setSaving(false);
            realtimeState?.publishOperation?.({
                key: saveOperationKey,
                running: false,
            });
        }
    }, [
        item,
        request,
        effectiveSaving,
        realtimeState,
        saveOperationKey,
    ]);

    const viewerProduct = useMemo(() => {
        if (!linkedProduct?._id) return null;

        return {
            ...linkedProduct,
            _catalogReviewPurchasedNumber: item?.purchasedNumber,
            _catalogReviewModelDimensions: modelDimensions,
            _catalogReviewDimensions: viewerDimensions || currentDimensions,
            _catalogReviewCatalogDimensions: initialDimensions,
            _catalogReviewOrientation: item?.geometryAnalysis?.axisPreset
                || item?.orientation
                || 'unknown',
        };
    }, [
        currentDimensions,
        initialDimensions,
        viewerDimensions,
        modelDimensions,
        item?.geometryAnalysis?.axisPreset,
        item?.orientation,
        item?.purchasedNumber,
        linkedProduct,
    ]);

    const toggleMediaView = useCallback(() => {
        if (activeView === '3d') {
            setActiveView('pricing');
            return;
        }

        if (!viewerProduct?._id || !viewerProduct?.modelPath) return;
        setActiveView('3d');
    }, [activeView, viewerProduct]);

    useEffect(() => {
        if (previousViewRef.current === activeView) return;

        previousViewRef.current = activeView;
        request?.onViewChange?.({
            view: activeView,
            purchasedNumber: Number(item?.purchasedNumber || currentNumber || 0),
            item,
            linkedProduct,
        });
    }, [
        activeView,
        currentNumber,
        item,
        linkedProduct,
        request,
    ]);

    const resetInitial = useCallback(() => {
        const normalized = normalizeDimensions(modelDimensions);

        setDimensionSource('model');
        setForm({
            height: normalized?.height ?? '',
            width: normalized?.width ?? '',
            thickness: normalized?.thickness ?? '',
            price: initial.basePrice ?? '',
            comment: item?.reviewComment || '',
        });

        const height = Number(normalized?.height || 0);
        const width = Number(normalized?.width || 0);
        ratioRef.current = height > 0 && width > 0
            ? width / height
            : null;
    }, [
        initial.basePrice,
        item?.reviewComment,
        modelDimensions,
    ]);

    const renderPricingMedia = () => (
        <div
            className={[
                'catalogReview__pricingReviewMedia',
                activeView === '3d' ? 'is-3d' : 'is-photo',
            ].join(' ')}
            data-catalog-sync-scope={`pricing-media-${currentNumber}-${activeView}`}
            onMouseDown={(event) => event.stopPropagation()}
        >
            {activeView === 'pricing' ? (
                item.imagePath ? (
                    <img
                        src={resolveAnyStaticUrl(item.imagePath)}
                        alt={manualReview ? linkedProductName : `Форма №${item.purchasedNumber}`}
                    />
                ) : (
                    <span>Нет изображения</span>
                )
            ) : null}

            {activeView === '3d' && viewerProduct ? (
                <AdminProduct3DPreview
                    isOpen
                    embedded
                    product={viewerProduct}
                    products={[viewerProduct]}
                    categories={[]}
                    mode="viewer"
                    modelDimensions={viewerDimensions || currentDimensions}
                    blankDimensions={viewerDimensions || currentDimensions}
                    showBlankFrame={authoritativePricingDraft?.showBlankFrame !== false}
                    viewerTitle={manualReview ? `3D · ${linkedProductName}` : `3D формы №${item?.purchasedNumber || currentNumber}`}
                    onClose={closeReview}
                    realtimeOperations={realtimeOperations}
                    onRealtimeOperation={realtimeState?.publishOperation || null}
                    sharedViewState={authoritativePricingDraft}
                    onSharedViewStateChange={(field, value) => publishDraft(field, value)}
                />
            ) : null}

            {activeView === '3d' && !viewerProduct ? (
                <div className="catalogReview__pricingReviewMediaMissing">
                    <strong>Нужна 3D-модель</strong>
                    <span>У связанного товара нет доступного файла модели.</span>
                </div>
            ) : null}
        </div>
    );

    return (
        <div
            className="catalogReview__pricingReviewBackdrop"
            data-catalog-sync-scope={`pricing-review-${currentNumber}`}
            data-admin-realtime-state-only="true"
            role="presentation"
            onMouseDown={() => {
                if (!effectiveSaving) closeReview();
            }}
        >
            <div
                className={[
                    'catalogReview__pricingReviewModal',
                    'catalogReview__pricingReviewModal--pricing',
                    activeView === '3d' ? 'is-media-3d' : 'is-media-photo',
                ].join(' ')}
                role="dialog"
                aria-modal="true"
                data-catalog-scroll-id={`pricing-modal-${currentNumber}-${activeView}`}
                data-catalog-sync-scope={`pricing-${currentNumber}-${activeView}`}
                aria-label={manualReview ? `Проверка цены · ${linkedProductName}` : `Проверка цены формы №${currentNumber}`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="catalogReview__pricingReviewHeader">
                    <div className="catalogReview__pricingReviewHeaderMain">
                        <small>Проверка размера и фрезеровки</small>
                        <h2>{linkedProductName || (manualReview ? 'Товар на ручной проверке' : `Купленная форма №${currentNumber}`)}</h2>
                        <span>
                            {manualReview ? 'Ручная проверка товара' : `Форма №${currentNumber}`}
                            {linkedProduct?.article
                                ? ` · Артикул: ${linkedProduct.article}`
                                : ''}
                            {linkedProductCategory
                                ? ` · Категория: ${linkedProductCategory}`
                                : ''}
                            {item ? ` · ${sourceLabel(item)}` : ' · Загрузка'}
                        </span>

                    </div>

                    <div className="catalogReview__pricingReviewHeaderActions">
                        {queueNumbers.length ? (
                            <div
                                className="catalogReview__pricingReviewQueueNav"
                                aria-label="Навигация по формам"
                            >
                                <button
                                    type="button"
                                    className="is-queue-arrow"
                                    disabled={!hasPrevious || loading || effectiveSaving}
                                    onClick={movePrevious}
                                    aria-label="Предыдущая форма"
                                    title="Предыдущая форма"
                                >
                                    ‹
                                </button>
                                <strong>
                                    {currentIndex + 1} / {queueNumbers.length}
                                </strong>
                                <button
                                    type="button"
                                    className="is-queue-arrow"
                                    disabled={!hasNext || loading || effectiveSaving}
                                    onClick={moveNext}
                                    aria-label="Следующая форма"
                                    title="Следующая форма"
                                >
                                    ›
                                </button>
                            </div>
                        ) : null}
                        {linkedProduct?.modelPath ? (
                            <button
                                type="button"
                                onClick={toggleMediaView}
                                disabled={loading || effectiveSaving}
                            >
                                {activeView === '3d' ? 'Показать фото' : 'Открыть 3D'}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="is-close"
                            onClick={closeReview}
                            disabled={effectiveSaving}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="catalogReview__pricingReviewLoading">
                        Загрузка формы…
                    </div>
                ) : null}

                {!loading && item ? (
                    <div
                        className="catalogReview__pricingReviewBody"
                        data-catalog-scroll-id={`pricing-body-${currentNumber}`}
                    >
                        <section className="catalogReview__pricingReviewPreview">
                            {renderPricingMedia()}

                            <div className="catalogReview__pricingReviewPreviewStats">
                                <div>
                                    <span>Первоначальная цена</span>
                                    <strong>{formatMoney(initial.basePrice)}</strong>
                                </div>
                                <div>
                                    <span>Оценочный диапазон</span>
                                    <strong>
                                        {formatMoney(estimateRange.min)}
                                        {' — '}
                                        {formatMoney(estimateRange.max)}
                                    </strong>
                                </div>
                                <div>
                                    <span>Статус</span>
                                    <strong>{reviewStatusLabel(item.reviewStatus)}</strong>
                                </div>
                                <div>
                                    <span>Последнюю цену менял</span>
                                    <strong>{lastPriceEdit?.userName || '—'}</strong>
                                    {lastPriceEdit?.createdAt ? (
                                        <small>{formatAuditTime(lastPriceEdit.createdAt)}</small>
                                    ) : null}
                                </div>
                            </div>
                        </section>

                        <section className="catalogReview__pricingReviewEditor">
                            <div className="catalogReview__pricingReviewLive">
                                <div>
                                    <span>Текущая цена</span>
                                    <strong>{formatMoney(form.price)}</strong>
                                </div>
                                <div>
                                    <span>Объём</span>
                                    <strong>
                                        {currentVolume !== null
                                            ? `${currentVolume.toFixed(4)} м³`
                                            : '—'}
                                    </strong>
                                </div>
                            </div>

                            <div className={[
                                'catalogReview__modelDimensionStatus',
                                modelDimensions ? 'is-ready' : 'is-missing',
                                dimensionSource === 'custom' ? 'is-custom' : 'is-model',
                            ].join(' ')}>
                                <div>
                                    <span>Размер 3D-модели</span>
                                    <strong>
                                        {modelDimensions
                                            ? formatSize(modelDimensions)
                                            : 'Нужна 3D-модель'}
                                    </strong>
                                </div>
                                <small>
                                    {!modelDimensions
                                        ? 'Размер геометрии ещё не получен.'
                                        : (
                                            dimensionSource === 'custom'
                                                ? 'Инпуты изменены вручную.'
                                                : 'Инпуты заполнены по измеренной геометрии.'
                                        )}
                                </small>
                            </div>

                            <label className="catalogReview__proportionalToggle">
                                <input
                                    type="checkbox"
                                    checked={proportional}
                                    onFocus={(event) => focusDraftField(event, 'proportional')}
                                    onBlur={() => blurDraftField('proportional')}
                                    disabled={effectiveSaving}
                                    onChange={(event) => toggleProportional(event.target.checked)}
                                />
                                <span>
                                    <strong>Пропорционально</strong>
                                    <small>
                                        Высота и ширина меняются вместе. Толщина отдельно.
                                    </small>
                                </span>
                            </label>

                            <div className="catalogReview__pricingReviewForm">
                                <label>
                                    <span>Высота, мм</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="10"
                                        value={form.height}
                                        disabled={effectiveSaving}
                                        onFocus={(event) => focusDraftField(event, 'height')}
                                        onBlur={() => blurDraftField('height')}
                                        onChange={(event) => changeDimension(
                                            'height',
                                            event.target.value
                                        )}
                                    />
                                </label>

                                <label>
                                    <span>Ширина, мм</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="10"
                                        value={form.width}
                                        disabled={effectiveSaving}
                                        onFocus={(event) => focusDraftField(event, 'width')}
                                        onBlur={() => blurDraftField('width')}
                                        onChange={(event) => changeDimension(
                                            'width',
                                            event.target.value
                                        )}
                                    />
                                </label>

                                <label>
                                    <span>Толщина, мм</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="10"
                                        value={form.thickness}
                                        disabled={effectiveSaving}
                                        onFocus={(event) => focusDraftField(event, 'thickness')}
                                        onBlur={() => blurDraftField('thickness')}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setDimensionSource('custom');
                                            setForm((previous) => ({
                                                ...previous,
                                                thickness: value,
                                            }));
                                            publishDraft('dimensionSource', 'custom');
                                            publishDraft('thickness', value);
                                        }}
                                    />
                                </label>

                                <label>
                                    <span>Фрезеровка, ₽</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="500"
                                        value={form.price}
                                        disabled={effectiveSaving}
                                        onFocus={(event) => focusDraftField(event, 'price')}
                                        onBlur={() => blurDraftField('price')}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setForm((previous) => ({
                                                ...previous,
                                                price: value,
                                            }));
                                            publishDraft('price', value);
                                        }}
                                    />
                                </label>

                                <label className="is-wide">
                                    <span>Комментарий</span>
                                    <textarea
                                        value={form.comment}
                                        disabled={effectiveSaving}
                                        placeholder="Почему изменён размер или цена"
                                        onFocus={(event) => focusDraftField(event, 'comment')}
                                        onBlur={() => blurDraftField('comment')}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setForm((previous) => ({
                                                ...previous,
                                                comment: value,
                                            }));
                                            publishDraft('comment', value);
                                        }}
                                    />
                                </label>
                            </div>

                            {!modelDimensions ? (
                                <div className="catalogReview__pricingReviewValidation">
                                    Нужна измеренная 3D-модель. Запусти получение размеров в разделе «3D и размеры».
                                </div>
                            ) : null}

                            {!valid ? (
                                <div className="catalogReview__pricingReviewValidation">
                                    Заполни высоту, ширину, толщину и цену.
                                </div>
                            ) : null}

                            {message ? (
                                <div className={`catalogReview__message catalogReview__message--${message.type}`}>
                                    {message.text}
                                </div>
                            ) : null}

                            <div className="catalogReview__pricingReviewActions">
                                <button
                                    type="button"
                                    className="is-success is-wide"
                                    disabled={!valid || effectiveSaving}
                                    onClick={() => save(changed ? 'edited' : 'approved')}
                                >
                                    {effectiveSaving
                                        ? 'Сохранение…'
                                        : (
                                            changed
                                                ? 'Сохранить правку и дальше'
                                                : 'Подтвердить и дальше'
                                        )}
                                </button>

                                <button
                                    type="button"
                                    className="is-warning"
                                    disabled={!valid || effectiveSaving}
                                    onClick={() => save('manual')}
                                >
                                    На ручную проверку
                                </button>

                                <button
                                    type="button"
                                    className="is-back"
                                    disabled={effectiveSaving || !hasPrevious}
                                    onClick={movePrevious}
                                >
                                    Назад
                                </button>

                                <button
                                    type="button"
                                    disabled={effectiveSaving || !hasNext}
                                    onClick={moveNext}
                                >
                                    Дальше
                                </button>

                                <button
                                    type="button"
                                    className="is-reset"
                                    disabled={effectiveSaving || !modelDimensions}
                                    onClick={resetInitial}
                                >
                                    Вернуть размер 3D-модели
                                </button>

                                {item.reviewStatus !== 'unchecked' ? (
                                    <button
                                        type="button"
                                        className="is-uncheck is-wide"
                                        disabled={effectiveSaving}
                                        onClick={resetToUnchecked}
                                    >
                                        Вернуть в непроверенные
                                    </button>
                                ) : null}
                            </div>
                        </section>
                    </div>
                ) : null}

                {!loading && !item ? (
                    <div className="catalogReview__pricingReviewLoading">
                        {message?.text || 'Форма не найдена'}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export const CatalogPricingReviewProvider = ({ children }) => {
    const [request, setRequest] = useState(null);
    const [currentNumber, setCurrentNumber] = useState(null);
    const [remotePatch, setRemotePatch] = useState(null);
    const [itemUpdate, setItemUpdate] = useState(null);
    const [realtimeState, setRealtimeState] = useState({
        users: [],
        connected: false,
        socketId: '',
        operations: {},
        workspace: null,
        publishOperation: null,
    });
    const requestSequenceRef = useRef(0);

    const closeCatalogPricingReview = useCallback((options = {}) => {
        if (options.notify !== false) {
            request?.onClose?.();
        }

        setRequest(null);
        setCurrentNumber(null);
        setRemotePatch(null);
    }, [request]);

    const openCatalogPricingReview = useCallback((options = {}) => {
        const purchasedNumber = Number(options.purchasedNumber || 0);
        if (!purchasedNumber) return;

        const queueNumbers = uniqueNumbers([
            ...(options.queueNumbers || []),
            purchasedNumber,
        ]);

        requestSequenceRef.current += 1;

        setRequest({
            ...options,
            requestId: requestSequenceRef.current,
            initialView: options.initialView === '3d' ? '3d' : 'pricing',
            purchasedNumber,
            queueNumbers,
        });
        setCurrentNumber(purchasedNumber);
    }, []);

    const syncCatalogPricingReview = useCallback((options = {}) => {
        if (options.open !== true) {
            setRequest(null);
            setCurrentNumber(null);
            setRemotePatch(null);
            return;
        }

        const purchasedNumber = Number(options.purchasedNumber || 0);
        if (!purchasedNumber) return;

        const queueNumbers = uniqueNumbers([
            ...(options.queueNumbers || []),
            purchasedNumber,
        ]);

        requestSequenceRef.current += 1;

        setRequest({
            ...options,
            requestId: requestSequenceRef.current,
            initialView: options.initialView === '3d' ? '3d' : 'pricing',
            purchasedNumber,
            queueNumbers,
            remoteSync: true,
        });
        setCurrentNumber(purchasedNumber);
    }, []);

    const applyRemoteCatalogPricingPatch = useCallback((payload = {}) => {
        if (
            payload.scope !== 'pricing'
            || !payload.field
            || !payload.entityNumber
        ) {
            return;
        }

        setRemotePatch({
            ...payload,
            receivedAt: Date.now(),
        });
    }, []);

    const applyCatalogPricingItemUpdate = useCallback((item) => {
        if (!item?.purchasedNumber) return;
        setItemUpdate({ item, receivedAt: Date.now() });
    }, []);

    const syncCatalogPricingRealtime = useCallback((nextState = {}) => {
        setRealtimeState((previous) => ({
            ...previous,
            ...nextState,
        }));
    }, []);

    const changeCurrentNumber = useCallback((nextNumber, options = {}) => {
        const number = Number(nextNumber || 0);
        if (!number) return;

        setCurrentNumber(number);
        request?.onCurrentNumberChange?.(number, options);
    }, [request]);

    const value = useMemo(() => ({
        isOpen: Boolean(request),
        currentNumber,
        openCatalogPricingReview,
        syncCatalogPricingReview,
        applyRemoteCatalogPricingPatch,
        applyCatalogPricingItemUpdate,
        syncCatalogPricingRealtime,
        closeCatalogPricingReview,
    }), [
        applyRemoteCatalogPricingPatch,
        applyCatalogPricingItemUpdate,
        syncCatalogPricingRealtime,
        closeCatalogPricingReview,
        currentNumber,
        openCatalogPricingReview,
        request,
        syncCatalogPricingReview,
    ]);

    return (
        <CatalogPricingReviewContext.Provider value={value}>
            {children}

            {request && currentNumber ? (
                <CatalogPricingReviewModal
                    key={request.requestId}
                    request={request}
                    currentNumber={currentNumber}
                    onCurrentNumberChange={changeCurrentNumber}
                    onClose={closeCatalogPricingReview}
                    remotePatch={remotePatch}
                    realtimeState={realtimeState}
                    itemUpdate={itemUpdate}
                />
            ) : null}
        </CatalogPricingReviewContext.Provider>
    );
};

export const useCatalogPricingReview = () => {
    const context = useContext(CatalogPricingReviewContext);

    if (!context) {
        throw new Error(
            'useCatalogPricingReview must be used inside CatalogPricingReviewProvider'
        );
    }

    return context;
};

export default CatalogPricingReviewProvider;
